import FirebaseAnalytics from "./FirebaseAnalytics";
import OKSpinAnalytics from "./OKSpinAnalytics";
import SelfBuildWebCount from "./SelfBuildWebCount";
import EagllwinAnalytics from "./EagllwinAnalytics";
import { SendAdjustWebEvent, SendAdjustWebRecharge, BindAdjustWebUser } from "./AdjustWebAnalytics";
import { shouldSkipPPRegistration } from "./RegistrationConversion";

const safelyDispatch = (target, callback) => {
  try {
    const pending = callback();
    if (pending && typeof pending.then === "function") {
      Promise.resolve(pending).catch(() => console.warn("[Analytics]", { target, reason: "dispatch_failed" }));
    }
  } catch (error) {
    console.warn("[Analytics]", { target, reason: "dispatch_failed" });
  }
};

const SendOtherAnalytics = (event, data, sendSelfBuildOnce) => {
  safelyDispatch("eagllwin", () => EagllwinAnalytics.GetInstance().SendEvent(event, { ...data }));
  //EagllwinAnalytics归因要把游客注册和账号注册区分出来
  if (event == "register2") {
    event = "register"
  }
  //matomo
  safelyDispatch("matomo", () => {
    if (!window._paq || !window._paq.push) return;
    if (typeof data == "object" && Object.keys(data).length > 0) {
      for (let key in data) {
        window._paq.push(["trackEvent", event, event, key, data[key]]);
      }
    } else {
      window._paq.push(["trackEvent", event, event]);
    }
  });
  //GA
  safelyDispatch("ga", () => { if (window.gtag) window.gtag("event", event, { ...data }); });
  //Firebase
  safelyDispatch("firebase", () => { if (FirebaseAnalytics.instance) return FirebaseAnalytics.instance.SendEvent(event, { ...data }); });

  //其他的一些统计
  safelyDispatch("okspin", () => { if (OKSpinAnalytics.instance) return OKSpinAnalytics.instance.SendEvent(event, { ...data }); });
  safelyDispatch("selfbuild", () => sendSelfBuildOnce
    ? SelfBuildWebCount.GetInstance().SendEventOnce(event, { ...data })
    : SelfBuildWebCount.GetInstance().SendEvent(event, { ...data }));
};

const SendEventToAll = (event, data = {}, sendSelfBuildOnce) => {
  const originalEvent = event;
  data = data || {};
  SendOtherAnalytics(event, data, sendSelfBuildOnce);
  if (shouldSkipPPRegistration(originalEvent, data)) return;
  if (event === "register2") event = "register";
  //JSBridge: AF or Adjust
  safelyDispatch("adjust_web", () => SendAdjustWebEvent(originalEvent, data));
  safelyDispatch("native_bridge", () => {
    if (window.JSBridge && window.JSBridge.SendEvent) window.JSBridge.SendEvent(event, JSON.stringify(data));
  });
};

export const SendEvent = (event, data) => {
  SendEventToAll(event, data, true);
};

export const SendEventEveryTime = (event, data) => {
  SendEventToAll(event, data, false);
};

const statusResult = (status, reason) => reason ? { status, reason } : { status };
const textValue = (value) => value == null ? "" : String(value).trim();

const isNativeContext = () => {
  if (window.JSBridge || window.JSInterface || window.SuWin8Bridge ||
      /GameApp|SKGames|AndroidApp/.test(window.navigator.userAgent)) return true;
  try {
    return Boolean(localStorage.getItem("fromApp"));
  } catch (error) {
    return false;
  }
};

const getNativeBridge = () => [window.JSBridge, window.JSInterface, window.SuWin8Bridge]
  .find((bridge) => bridge && typeof bridge.SendEvent === "function");

const canDispatchRecharge = (canDispatch) => {
  try {
    return typeof canDispatch !== "function" || canDispatch() !== false;
  } catch (error) {
    return false;
  }
};

export const SendRechargeAttribution = async (data = {}, {
  purchaseQueued = false,
  firstPurchaseQueued = false,
  skipOtherAnalytics = false,
  canDispatch,
} = {}) => {
  data = data || {};
  const otherAnalyticsDispatched = !skipOtherAnalytics && canDispatchRecharge(canDispatch);
  if (otherAnalyticsDispatched) SendOtherAnalytics("recharge", data, true);
  if (!isNativeContext()) {
    const result = await SendAdjustWebRecharge(data, { purchaseQueued, firstPurchaseQueued, canDispatch });
    return { ...result, otherAnalyticsDispatched };
  }

  const firstRequested = data.isFirstPurchase === true || data.is_first_purchase === true;
  const result = {
    transport: "native_bridge",
    purchase: statusResult(purchaseQueued ? "native_dispatched_unconfirmed" : "retry", "bridge_unavailable"),
    firstPurchase: firstPurchaseQueued ? statusResult("native_dispatched_unconfirmed")
      : firstRequested ? statusResult("retry", "bridge_unavailable") : statusResult("not_requested"),
    otherAnalyticsDispatched,
  };
  if (purchaseQueued) result.purchase = statusResult("native_dispatched_unconfirmed");
  if (purchaseQueued && (!firstRequested || firstPurchaseQueued)) return result;
  if (!canDispatchRecharge(canDispatch)) {
    if (!purchaseQueued) result.purchase = statusResult("retry", "account_changed");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = statusResult("retry", "account_changed");
    return result;
  }

  const revenue = Number(data.revenue == null ? data.value : data.revenue);
  const currency = textValue(data.currency).toUpperCase();
  const invalidReason = !textValue(data.transactionId || data.transaction_id) ? "missing_transaction_id"
    : !Number.isFinite(revenue) || revenue <= 0 ? "invalid_revenue"
      : !/^[A-Z]{3}$/.test(currency) ? "invalid_currency" : "";
  if (invalidReason) {
    if (!purchaseQueued) result.purchase = statusResult("blocked", invalidReason);
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = statusResult("blocked", invalidReason);
    return result;
  }

  const bridge = getNativeBridge();
  if (!canDispatchRecharge(canDispatch)) {
    if (!purchaseQueued) result.purchase = statusResult("retry", "account_changed");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = statusResult("retry", "account_changed");
    return result;
  }
  if (!bridge) return result;
  try {
    // A legacy Java void bridge cannot acknowledge SDK persistence or delivery.
    // Do not resend a purchase just to hand off a remaining first purchase.
    const event = purchaseQueued ? "first_purchase" : "recharge";
    const payload = firstPurchaseQueued && !purchaseQueued
      ? { ...data, isFirstPurchase: false, is_first_purchase: false } : data;
    bridge.SendEvent(event, JSON.stringify(payload));
    if (!purchaseQueued) result.purchase = statusResult("native_dispatched_unconfirmed");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = statusResult("native_dispatched_unconfirmed");
  } catch (error) {
    if (!purchaseQueued) result.purchase = statusResult("retry", "bridge_failed");
    if (firstRequested && !firstPurchaseQueued) result.firstPurchase = statusResult("retry", "bridge_failed");
  }
  return result;
};

export const BindUser = (rid) => {
  safelyDispatch("adjust_bind_user", () => BindAdjustWebUser(rid));
  safelyDispatch("native_bind_user", () => {
    if (window.JSBridge && window.JSBridge.SetRid) window.JSBridge.SetRid(rid);
  });
};
